import { theme } from "@monthly/theme";
import { Stack, type StackProps, styled, Typography } from "@mui/material";
import type { ComponentType } from "react";

export const TableRow: ComponentType<StackProps> = styled(Stack)(
  ({ theme }) => ({
    flexDirection: "row",
    justifyContent: "space-between",
    cursor: "default",
    transition: "background-color 0.15s ease",
    borderBottom: `1px solid ${theme.palette.support[400]}`,
    minHeight: 40,
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),
);

export const TableCell: ComponentType<StackProps> = styled(Stack)({
  padding: "10px 16px",
  flex: 1,
  textAlign: "center",
  "&:first-of-type": {
    textAlign: "left",
  },
});

export type TableDiffRow = {
  category: string;
  actual: number;
  comparable: number;
  diff: number;
};

export type TableBalanceRow = {
  actual: number;
  diff: number;
  isPositive: boolean;
  ma: number;
  label: string;
};

type TableChangeRow = {
  category: string;
  amount: string;
  change: number;
};

type SpotTableRow =
  | TableDiffRow
  | TableChangeRow
  | TableBalanceRow
  | Record<string, string | number>;

type SpotTableProps = {
  columns: string[];
  rows: SpotTableRow[];
  decimalPlaces?: number;
  isPercentage?: boolean;
};

export function TwoColumnLayoutTable({
  columns,
  rows,
  decimalPlaces = 2,
  isPercentage = false,
}: SpotTableProps) {
  // Get data keys from the first row (excluding 'category')
  const dataKeys =
    rows.length > 0
      ? Object.keys(rows[0]).filter(
          (key) => key !== "category" && key !== "label",
        )
      : [];

  const isThreeColumnTable = columns.length === 3;

  const formatCellValue = (
    value: string | number,
    key: string,
  ): string | number => {
    if (typeof value === "number") {
      const safeDecimalPlaces = Number.isInteger(decimalPlaces)
        ? Math.max(decimalPlaces, 0)
        : 2;
      // Truncate to configured decimal places without rounding.
      const multiplier = 10 ** safeDecimalPlaces;
      const truncated = Math.trunc(value * multiplier) / multiplier;
      const formattedValue = truncated.toFixed(safeDecimalPlaces);

      const isDiffColumn =
        key === "diff" || key === "change" || key === "cambio";

      // Add "%" to last column ONLY when:
      // - In absolute mode (!isPercentage)
      // - It's a 3-column table (Label - Comparable - Change)
      // - Last column is a change column
      const shouldAddPercentToChange = !isPercentage && isThreeColumnTable && isDiffColumn;

      if (isDiffColumn) {
        // Use '%' for absolute mode 3-column change tables, 'p' for vertical (percentage points), otherwise no suffix
        const suffix = shouldAddPercentToChange ? "%" : isPercentage ? "p" : "";
        return `${formattedValue}${suffix}`;
      }

      return formattedValue;
    }
    return value;
  };

  const getCellColor = (value: string | number, key: string) => {
    if (typeof value === "number" && (key === "diff" || key === "change")) {
      return theme.palette.primary.main;
    }
    return undefined;
  };

  return (
    <Stack>
      <TableRow sx={{ "&:hover": { backgroundColor: "transparent" } }}>
        <TableCell />
        {columns.map((col) => (
          <TableCell key={col}>
            <Typography
              variant="body2"
              color="secondary.dark"
              textAlign="center"
              sx={(theme) => ({
                fontWeight: theme.typography.fontWeightSemiBold,
              })}
            >
              {col}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
      {rows.map((row, index) => {
        const rowKey = (
          "category" in row
            ? `${row.category}-${index}`
            : "label" in row
              ? `${row.label}-${index}`
              : `row-${index}`
        ) as string;
        const rowLabel = (
          "category" in row ? row.category : "label" in row ? row.label : ""
        ) as string;
        return (
          <TableRow key={rowKey}>
            <TableCell>
              <Typography
                variant="caption"
                color="text.primary"
                textAlign="left"
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.4,
                }}
              >
                {rowLabel}
              </Typography>
            </TableCell>
            {dataKeys.map((key) => {
              if (key === "isPositive") {
                return null; // Skip rendering this cell, it's only for color logic
              }
              const value = row[key as keyof typeof row];
              const cellColor = getCellColor(value as string | number, key);
              return (
                <TableCell key={key}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    textAlign="center"
                    sx={(theme) => ({
                      color: cellColor || "currentColor",
                      fontWeight: theme.typography.fontWeightSemiBold,
                    })}
                  >
                    {formatCellValue(value as string | number, key)}
                  </Typography>
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </Stack>
  );
}
